import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../../utils';
import Icon from '../../components/common/Icon';

/* The company's legal papers — TRN certificate, trade licence, bank certificate — on Edit
   company. Only an admin of the company gets the list (the API answers 403 otherwise, and then
   this renders nothing), and only on a Gulf BU — a Belgian customer gets an empty list. The supplier portal shows the same block for a supplier we pay, against
   its own endpoint (apiBase='/supplier/legal-docs') — same files, a different gate. Each row uploads on its own: picking a file sends it straight away, so
   there is no second "save" to forget. The files are private; "View" asks for a short-lived link. */

const LABEL_KEYS = {
  trn: 'docTrn',
  trade_licence: 'docTradeLicence',
  bank_certificate: 'docBankCertificate',
};

const DocRow = ({ doc, onUploaded, apiBase }) => {
  const { t } = useTranslation();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    const body = new FormData();
    body.append('file', file);
    try {
      const response = await fetch(`/api${apiBase}/${doc.key}`, {
        method: 'POST', body, credentials: 'include',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'somethingWentWrong');
      onUploaded(data.docs);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const view = async () => {
    try {
      const data = await apiRequest('GET', `${apiBase}/${doc.key}/url`);
      if (data?.url) window.open(data.url, '_blank', 'noopener');
    } catch (e) {
      setError(e.message);
    }
  };

  const uploaded = !!doc.uploaded_at;
  return (
    <div className="d-flex align-items-center justify-content-between py-2 border-top">
      <div className="me-2" style={{ minWidth: 0 }}>
        <div className="fw-semibold">
          <Icon name={uploaded ? 'check-circle' : 'document'} theme={uploaded ? 'success' : 'muted'} className="me-2" />
          {t(LABEL_KEYS[doc.key])}
        </div>
        <div className="small text-muted text-truncate">
          {uploaded ? t('uploadedOn', { date: (doc.uploaded_at || '').slice(0, 10) }) : t('notUploadedYet')}
        </div>
        {error && <div className="small text-danger">{t(error)}</div>}
      </div>
      <div className="d-flex gap-2 flex-shrink-0">
        {uploaded && (
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={view}>{t('view')}</button>
        )}
        <button type="button" className="btn btn-sm btn-outline-primary" disabled={busy}
                onClick={() => input.current && input.current.click()}>
          {busy ? t('uploading') : (uploaded ? t('replace') : t('upload'))}
        </button>
        <input ref={input} type="file" accept="application/pdf,image/png,image/jpeg" hidden
               onChange={(e) => upload(e.target.files && e.target.files[0])} />
      </div>
    </div>
  );
};

/* only: the doc keys to show (the supplier portal asks for just the trade licence when the
   request says so); onChange: told after an upload, so a page can re-read its own checklist. */
const LegalDocuments = ({ apiBase = '/company-legal-docs', hintKey = 'legalDocumentsHint', only = null, onChange = null }) => {
  const { t } = useTranslation();
  const [docs, setAllDocs] = useState(null);
  const setDocs = (next) => {
    setAllDocs(next);
    if (onChange) onChange();
  };

  const loaded = !!docs;

  useEffect(() => {
    apiRequest('GET', apiBase)
      .then((data) => setAllDocs(data.docs || []))
      .catch(() => setAllDocs(null));
  }, [apiBase]);

  // The "please upload your documents" mail lands on /edit-company#legal-documents; the block
  // only exists once its list is in, so the browser's own jump to the anchor has nothing to hit.
  useEffect(() => {
    if (loaded && window.location.hash === '#legal-documents') {
      document.getElementById('legal-documents')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [loaded]);

  // An empty list = these papers don't apply to this company (a non-Gulf BU) → no block at all.
  const shown = (docs || []).filter((d) => !only || only.includes(d.key));
  if (!shown.length) return null;
  return (
    <div className="mt-4" id="legal-documents">
      <h5 className="mb-1">{t('legalDocuments')}</h5>
      <div className="small text-muted mb-2">{t(hintKey)}</div>
      {shown.map((doc) => <DocRow key={doc.key} doc={doc} onUploaded={setDocs} apiBase={apiBase} />)}
    </div>
  );
};

export default LegalDocuments;
